import React, { useEffect } from 'react';
import { ArrowLeft, Phone, Mail, MapPin } from 'lucide-react';
export function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy & Communication Policy | BRIXEN CREDIT LLC';
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900" data-id="element-950">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10" data-id="element-951">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-952">
          <a href="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 font-medium transition-colors" data-id="element-953">
            <ArrowLeft className="w-5 h-5 mr-2" data-id="element-954" />
            Back to Home
          </a>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-955">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12" data-id="element-956">
          {/* Page Title */}
          <div className="border-b border-gray-100 pb-8 mb-8" data-id="element-957">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-958">
              Privacy & Communication Policy
            </h1>
            <p className="text-sm text-gray-500 font-medium" data-id="element-959">
              Last Updated: January 30, 2026
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12" data-id="element-960">
            <h2 className="text-lg font-bold text-gray-900 mb-4" data-id="element-961">
              Table of Contents
            </h2>
            <nav data-id="element-962">
              <ul className="space-y-2 text-sm md:text-base" data-id="element-963">
                {[{
                id: 'introduction',
                label: '1. Introduction'
              }, {
                id: 'personal-info',
                label: '2. Personal Information We Collect'
              }, {
                id: 'purpose',
                label: '3. Purpose of Data Collection'
              }, {
                id: 'sharing',
                label: '4. Information Sharing & Disclosure'
              }, {
                id: 'security',
                label: '5. Data Security & Storage'
              }, {
                id: 'communication',
                label: '6. Multichannel Communication Consent'
              }, {
                id: 'responsibilities',
                label: '7. Client Responsibilities'
              }, {
                id: 'termination',
                label: '8. Right to Terminate Services'
              }, {
                id: 'updates',
                label: '9. Policy Updates'
              }, {
                id: 'contact',
                label: '10. Contact Information'
              }].map(item => <li key={item.id} data-id="element-964">
                    <a href={`#${item.id}`} onClick={e => scrollToSection(e, item.id)} className="text-purple-600 hover:text-purple-800 hover:underline transition-colors" data-id="element-965">
                      {item.label}
                    </a>
                  </li>)}
              </ul>
            </nav>
          </div>

          {/* Content Sections */}
          <div className="prose prose-lg max-w-none text-gray-700 space-y-12" data-id="element-966">
            <section id="introduction" className="scroll-mt-24" data-id="element-967">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-968">
                1. Introduction
              </h2>
              <p className="mb-4" data-id="element-969">
                BRIXEN CREDIT LLC ("Company," "we," "us," or "our") is committed
                to protecting the privacy, confidentiality, and security of your
                personal and financial information. This Privacy & Communication
                Policy explains in detail how we collect, use, store, safeguard,
                and disclose personal data when you visit our website, request
                information, submit forms, schedule consultations, enroll in our
                services, or communicate with us through any channel.
              </p>
              <p className="mb-4" data-id="element-970">
                By accessing our website, submitting personal information,
                authorizing communications, or enrolling in any program, you
                acknowledge, understand, and agree to the practices described in
                this Policy. If you do not agree, you should not use our website
                or services.
              </p>
              <p data-id="element-971">
                This Policy operates in conjunction with our Terms of Service
                and Refund & Cancellation Policy.
              </p>
            </section>

            <section id="personal-info" className="scroll-mt-24" data-id="element-972">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-973">
                2. Personal Information We Collect
              </h2>
              <p className="mb-4" data-id="element-974">
                To properly perform credit restoration, compliance, and
                consulting services, the Company may collect, store, and process
                Personally Identifiable Information (PII) and sensitive personal
                data, including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-975">
                <li data-id="element-976">Full legal name</li>
                <li data-id="element-977">Social Security Number (SSN)</li>
                <li data-id="element-978">Date of birth</li>
                <li data-id="element-979">Residential and mailing address</li>
                <li data-id="element-980">Telephone number(s) and email address</li>
                <li data-id="element-981">Credit reports and credit monitoring credentials</li>
                <li data-id="element-982">Financial and payment information</li>
                <li data-id="element-983">Government-issued documentation when required</li>
                <li data-id="element-984">
                  Any documentation, correspondence, or records provided by the
                  Client related to credit accounts, disputes, or identity
                  verification
                </li>
              </ul>
              <p className="mb-4" data-id="element-985">
                This information is collected solely for legitimate business
                purposes, including execution of authorized services, regulatory
                compliance, identity verification, billing, fraud prevention,
                and fulfillment of federal reporting obligations.
              </p>
              <p data-id="element-986">
                The Company does not knowingly collect information from
                individuals under the age of 18.
              </p>
            </section>

            <section id="purpose" className="scroll-mt-24" data-id="element-987">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-988">
                3. Purpose of Data Collection
              </h2>
              <p className="mb-4" data-id="element-989">
                The Client's personal data is used strictly and exclusively for
                the following purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-990">
                <li data-id="element-991">Conducting credit audits, reviews, and analysis</li>
                <li data-id="element-992">
                  Drafting, managing, submitting, and tracking dispute letters
                  and reinvestigation requests
                </li>
                <li data-id="element-993">
                  Communicating with credit bureaus, creditors, debt collectors,
                  and data furnishers
                </li>
                <li data-id="element-994">
                  Managing the Client's file within secure internal software
                  platforms
                </li>
                <li data-id="element-995">
                  Filing authorized reports or complaints with federal agencies,
                  including the FTC and CFPB, when applicable
                </li>
                <li data-id="element-996">
                  Providing account updates, service notifications, reminders,
                  and educational or informational content
                </li>
                <li data-id="element-997">
                  Processing payments, billing, compliance reviews, fraud
                  prevention, and internal record-keeping
                </li>
              </ul>
              <p data-id="element-998">
                The Company does not sell, rent, lease, or trade Client data to
                third parties for marketing or advertising purposes under any
                circumstances.
              </p>
            </section>

            <section id="sharing" className="scroll-mt-24" data-id="element-999">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1000">
                4. Information Sharing & Disclosure
              </h2>
              <p className="mb-4" data-id="element-1001">
                The Company may share Client information only when strictly
                necessary and only with trusted third parties for legitimate
                service-related or legal purposes, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1002">
                <li data-id="element-1003">Credit bureaus: Equifax, Experian, TransUnion</li>
                <li data-id="element-1004">
                  Secondary or specialty bureaus: LexisNexis, Innovis,
                  ChexSystems, and similar agencies
                </li>
                <li data-id="element-1005">Credit monitoring services authorized by the Client</li>
                <li data-id="element-1006">
                  Federal agencies such as the Federal Trade Commission (FTC)
                  and Consumer Financial Protection Bureau (CFPB) when
                  submitting complaints, escalations, or identity theft reports
                </li>
                <li data-id="element-1007">
                  Legal authorities, courts, regulators, or law enforcement when
                  required by law, subpoena, court order, or regulatory request
                </li>
              </ul>
              <p data-id="element-1008">
                All third parties receiving Client data are required to handle
                such data in compliance with applicable privacy, security, and
                confidentiality laws.
              </p>
            </section>

            <section id="security" className="scroll-mt-24" data-id="element-1009">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1010">
                5. Data Security & Storage
              </h2>
              <p className="mb-4" data-id="element-1011">
                The Company implements administrative, technical, and physical
                safeguards designed to protect Client information against
                unauthorized access, misuse, disclosure, alteration, or
                destruction. These measures may include secure servers,
                encrypted data storage, access controls, and internal compliance
                procedures.
              </p>
              <p className="mb-4" data-id="element-1012">
                However, no method of data transmission or electronic storage is
                100% secure. By using our services, the Client acknowledges and
                accepts the inherent risks associated with internet-based data
                transmission.
              </p>
              <p data-id="element-1013">
                The Company disclaims liability for unauthorized access beyond
                its reasonable control.
              </p>
            </section>

            <section id="communication" className="scroll-mt-24" data-id="element-1014">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1015">
                6. Multichannel Communication Consent (TCPA & TSR Compliance)
              </h2>
              <p className="mb-4" data-id="element-1016">
                By submitting your information, requesting services, or
                enrolling in any program, you provide Express Written Consent
                authorizing BRIXEN CREDIT LLC and its agents, representatives,
                or service providers to contact you through multiple
                communication channels, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1017">
                <li data-id="element-1018">
                  Telephone calls, including calls made using automated dialing
                  systems or prerecorded messages
                </li>
                <li data-id="element-1019">
                  Text messages and WhatsApp, for case updates, document
                  requests, reminders, confirmations, and customer support
                </li>
                <li data-id="element-1020">
                  Email communications, including transactional emails, account
                  notices, educational materials, and service-related updates
                </li>
              </ul>
              <p className="mb-4" data-id="element-1021">
                This consent is provided in compliance with the Telephone
                Consumer Protection Act (TCPA) and the Telemarketing Sales Rule
                (TSR).
              </p>
              <p className="mb-4" data-id="element-1022">
                Consent is not a condition of purchase and may be revoked at any
                time by:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1023">
                <li data-id="element-1024">
                  Emailing:{' '}
                  <a href="mailto:soporte@brixencredit.com" className="text-purple-600 hover:underline" data-id="element-1025">
                    soporte@brixencredit.com
                  </a>
                </li>
                <li data-id="element-1026">Replying "STOP" to any SMS or messaging communication</li>
              </ul>
              <p className="mb-4" data-id="element-1027">
                Standard message and data rates may apply depending on the
                Client's carrier.
              </p>
              <p className="font-medium text-gray-900" data-id="element-1028">
                Mobile information will not be shared with third parties or
                affiliates for marketing or promotional purposes. Text messaging
                originator opt-in data and consent will not be shared with any
                third parties.
              </p>
            </section>

            <section id="responsibilities" className="scroll-mt-24" data-id="element-1029">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1030">
                7. Client Responsibilities
              </h2>
              <p className="mb-4" data-id="element-1031">The Client is responsible for:</p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1032">
                <li data-id="element-1033">Providing accurate, truthful, and complete information</li>
                <li data-id="element-1034">
                  Promptly updating the Company if personal or contact
                  information changes
                </li>
                <li data-id="element-1035">
                  Safeguarding login credentials and access to third-party
                  monitoring services
                </li>
              </ul>
              <p data-id="element-1036">
                Providing false, misleading, or incomplete information may
                result in immediate service suspension or termination, and may
                expose the Client to legal liability in cases involving federal
                reporting.
              </p>
            </section>

            <section id="termination" className="scroll-mt-24" data-id="element-1037">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1038">
                8. Right to Terminate Services
              </h2>
              <p className="mb-4" data-id="element-1039">
                The Company reserves the right, at its sole discretion, to
                suspend or terminate services without notice due to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4" data-id="element-1040">
                <li data-id="element-1041">Non-payment or failed billing</li>
                <li data-id="element-1042">Lack of cooperation or repeated non-compliance</li>
                <li data-id="element-1043">Fraudulent activity or misrepresentation</li>
                <li data-id="element-1044">
                  Violation of this Policy, the Terms of Service, or any related
                  agreement
                </li>
              </ul>
              <p data-id="element-1045">
                Termination does not relieve the Client of any outstanding
                financial obligations.
              </p>
            </section>

            <section id="updates" className="scroll-mt-24" data-id="element-1046">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1047">
                9. Policy Updates
              </h2>
              <p data-id="element-1048">
                The Company reserves the right to modify or update this Privacy
                & Communication Policy at any time. Continued use of the website
                or services after updates are posted constitutes acceptance of
                the revised Policy.
              </p>
            </section>

            <section id="contact" className="scroll-mt-24" data-id="element-1049">
              <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1050">
                10. Contact Information
              </h2>

              <div className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1051">
                <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1052">
                  BRIXEN CREDIT LLC
                </h3>
                <div className="space-y-4" data-id="element-1053">
                  <div className="flex items-start" data-id="element-1054">
                    <Phone className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1055" />
                    <div data-id="element-1056">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1057">
                        Phone
                      </span>
                      <a href="tel:786-738-6588" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-1058">
                        786-738-6588
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1059">
                    <Mail className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1060" />
                    <div data-id="element-1061">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1062">
                        Email
                      </span>
                      <a href="mailto:soporte@brixencredit.com" className="text-lg text-gray-900 hover:text-purple-600 transition-colors" data-id="element-1063">
                        soporte@brixencredit.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start" data-id="element-1064">
                    <MapPin className="w-5 h-5 text-purple-600 mt-1 mr-3 flex-shrink-0" data-id="element-1065" />
                    <div data-id="element-1066">
                      <span className="block text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1067">
                        Address
                      </span>
                      <span className="text-lg text-gray-900 block" data-id="element-1068">
                        390 NE 191ST ST, MIAMI, FL 33179
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>;
}